// Records who created or edited a configuration, and what an edit changed.
//
// Kevin, 2026-10-06: an edit by someone who is not the creator emails the creator with what
// each field was, what it became, who changed it and when. The endpoint (api/config-change)
// keeps the record and decides whether anyone is told; this is only the call.
//
// EVERY CREATE IS REPORTED TOO, even though it emails nobody: it is what tells a later edit
// who the creator was. NetSuite cannot -- every cpq2 write is the same integration user.
//
// FIRE AND FORGET, like reportMissingParts: it runs after the item is saved, so nothing here
// may turn a save that worked into an error. keepalive, because Save & Submit closes this tab
// on its way back to the sales order.
import { getLaunchContext } from "./launchContext";
import type { ConfigChange } from "./configDiff";

// A whole-configuration rewrite is still well under keepalive's 64 KB body cap at this size.
const MAX_CHANGES = 300;

export type ConfigChangeReport = {
  action: "create" | "edit";
  itemId: string;
  itemName?: string | null;
  /** The part number before an edit renamed it. */
  previousItemName?: string | null;
  product: string;
  /** What an edit changed. null when the before-values are not known (the item was not
   *  loaded from its saved snapshot): recorded as such, never as "no changes", and mailed
   *  to nobody. */
  changes?: ConfigChange[] | null;
};

export function reportConfigChange(report: ConfigChangeReport): void {
  try {
    const ctx = getLaunchContext();
    const changes = report.changes ? report.changes.slice(0, MAX_CHANGES) : report.changes;
    void fetch("/api/config-change", {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...report,
        changes,
        truncated: !!report.changes && report.changes.length > MAX_CHANGES,
        user: { id: ctx.userId, name: ctx.userName },
        // The order being worked in decides ownership: whoever creates the sales order is the
        // creator from then on, and an UNSAVED one (no id yet) is the editor's own.
        launch: { originType: ctx.originType, transactionId: ctx.transactionId },
      }),
    }).catch(() => {
      // The endpoint answers 200 for its own failures; this is the network being gone.
    });
  } catch {
    // Never let the record of a save become a problem for the save.
  }
}
